import { useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { IconShieldLockFilled, IconTruckFilled } from '@tabler/icons-react';
import { useSmallerThanMD } from 'components/shared/hooks/useMediaQuery';

import { BottomSheet } from './BottomSheet';

export const BuyWithConfidence = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const smallerThanMD = useSmallerThanMD();
  const [isVerySmall, setIsVerySmall] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;

    const checkWidth = () => {
      // iPhone SE and iPhone 13 Mini screen resolution is right at 375,
      // which the current breakpoints don't handle.
      setIsVerySmall(window.innerWidth <= 375);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    return () => window.removeEventListener('resize', checkWidth);
  }, [isModalOpen]);

  const modalContent = (
    <Col className="w-full max-w-138 self-center px-8 py-8 md:px-3 md:py-0">
      <Col className="items-center gap-6">
        <Col className="w-full items-center gap-2">
          <Image
            src={
              isVerySmall
                ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764962388442-16990.png?preset=square-128&service=cosmos-cache-friendly&dpr=2'
                : 'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764882992132-81635.png?preset=square-128&service=cosmos-cache-friendly&dpr=2'
            }
            alt="Opendoor Guarantee"
            width={isVerySmall ? 60 : 82}
            height={isVerySmall ? 60 : 82}
          />
          <Col className="w-full items-center gap-1">
            <Text
              as="h2"
              className="text-center text-subheader-lg text-od-content-primary md:text-header-sm"
            >
              The Opendoor Guarantee
            </Text>
            <Text
              as="p"
              className="text-center text-body-md text-od-content-tertiary md:text-body-lg"
            >
              Included with this home
            </Text>
          </Col>
        </Col>

        <Col className="w-full gap-6">
          <Row className="items-start gap-2 rounded-lg bg-od-bg-secondary p-3">
            <Box className="shrink-0">
              <IconTruckFilled size={24} color="var(--color-od-content-accent-brand)" />
            </Box>
            <Col className="flex-1 gap-1">
              <Text as="p" className="text-label-sm text-od-content-primary md:text-label-md">
                Early Move-In
              </Text>
              <Text as="p" className="text-body-sm text-od-content-secondary md:text-body-md">
                Move in up to 7 days early, helpful for scheduling movers and deliveries.
              </Text>
            </Col>
          </Row>

          <Row className="items-start gap-2 rounded-lg bg-od-bg-secondary p-3">
            <Box className="shrink-0">
              <IconShieldLockFilled size={24} color="var(--color-od-content-accent-brand)" />
            </Box>
            <Col className="flex-1 gap-1">
              <Text as="p" className="text-label-sm text-od-content-primary md:text-label-md">
                100-Day Warranty
              </Text>
              <Text as="p" className="text-body-sm text-od-content-secondary md:text-body-md">
                Peace of mind with coverage on major systems and appliances for your first 100 days.
              </Text>
            </Col>
          </Row>

          <Row className="items-start gap-2 rounded-lg bg-od-bg-secondary p-3">
            <Box className="shrink-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.9756 3.54395C18.3563 3.54395 19.4756 4.66323 19.4756 6.04395V11.2588H11.8701L15.3633 7.7666C15.6561 7.4737 15.6562 6.99795 15.3633 6.70508C15.0704 6.41227 14.5946 6.41227 14.3018 6.70508L9.5293 11.4785C9.23642 11.7714 9.23646 12.2462 9.5293 12.5391L14.3018 17.3125C14.5947 17.6054 15.0704 17.6054 15.3633 17.3125C15.6561 17.0196 15.6562 16.5438 15.3633 16.251L11.8701 12.7588H19.4756V17.9834C19.4756 19.3641 18.3563 20.4834 16.9756 20.4834H7.02734C5.64663 20.4834 4.52734 19.3641 4.52734 17.9834V6.04395C4.52734 4.66323 5.64663 3.54395 7.02734 3.54395H16.9756Z"
                  fill="#0041E7"
                />
              </svg>
            </Box>
            <Col className="flex-1 gap-1">
              <Text as="p" className="text-label-sm text-od-content-primary md:text-label-md">
                Free Cancellation
              </Text>
              <Text as="p" className="text-body-sm text-od-content-secondary md:text-body-md">
                If you don’t love your new home, cancel anytime before closing at no cost.
              </Text>
            </Col>
          </Row>
        </Col>
      </Col>
    </Col>
  );

  return (
    <>
      <Box className="w-full">
        <Row
          onClick={() => setIsModalOpen(true)}
          aria-label="Open The Opendoor Guarantee details"
          analyticsName="marketplace-buy-with-confidence-button"
          className="w-full items-center justify-start gap-4 rounded-xl border border-[rgba(0,65,231,0.3)] bg-[linear-gradient(0deg,rgba(0,65,231,0.03)_0%,rgba(0,65,231,0.03)_100%),var(--color-od-bg-primary)] pb-6 pl-5 pr-6 pt-6 shadow-[0_2px_12px_0_rgba(0,65,231,0.04),0_10px_22px_0_rgba(0,65,231,0.04),0_0_0_2px_rgba(0,65,231,0.10),0_1px_6px_0_rgba(0,65,231,0.20)]"
        >
          <Image
            src="https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764882992133-33358.png?preset=square-128&service=cosmos-cache-friendly&dpr=2"
            alt="Opendoor Guarantee"
            width={56}
            height={56}
            className="shrink-0"
          />
          <Col className="flex-1 items-start gap-1">
            <Text as="p" className="text-left text-label-md text-od-content-primary">
              The Opendoor Guarantee
            </Text>
            <Text as="p" className="text-left text-body-sm text-od-content-secondary">
              Included with this home.{' '}
              <Text as="span" className="inline-block whitespace-nowrap text-body-sm underline">
                Learn more
              </Text>
            </Text>
          </Col>
        </Row>
      </Box>

      {smallerThanMD ? (
        <BottomSheet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          {modalContent}
        </BottomSheet>
      ) : (
        <Dialog.Root role="dialog" open={isModalOpen} onOpenChange={setIsModalOpen}>
          <Dialog>
            <Dialog.Title className="sr-only">The Opendoor Guarantee</Dialog.Title>
            {modalContent}
          </Dialog>
        </Dialog.Root>
      )}
    </>
  );
};
